// Отрисовка пиктограмм. Формы и их разбор — в icons.ts, там же разбор почему.

import { ICON_SHAPES, parseShape, type IconName } from '@smyslika/ui-tokens'

export type { IconName }

function Shape({ shape }: { shape: string }) {
  const s = parseShape(shape)
  if (s.kind === 'circle') return <circle cx={s.cx} cy={s.cy} r={s.r} />
  if (s.kind === 'rect') return <rect x={s.x} y={s.y} width={s.width} height={s.height} rx={s.rx} />
  return <path d={s.d} />
}

export interface IconProps {
  name: IconName
  size?: number
  /** В мелких контекстах макет разрешает 1.9–2. */
  strokeWidth?: number
  /** Задать, только если иконка несёт смысл сама по себе. */
  title?: string
}

export function Icon({ name, size = 24, strokeWidth, title }: IconProps) {
  // Правило из README: в мелком контексте обводка чуть толще, иначе линия
  // истончается до невидимости.
  const width = strokeWidth ?? (size <= 18 ? 1.9 : 1.75)

  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title == null ? 'presentation' : 'img'}
      aria-hidden={title == null ? true : undefined}
    >
      {title == null ? null : <title>{title}</title>}
      {ICON_SHAPES[name].map((shape) => (
        <Shape key={shape} shape={shape} />
      ))}
    </svg>
  )
}

/**
 * Радиусы плиток. Макет называет три размера прямо: 22 → 6 (строка
 * источника), 26 → 8 (метка проекта), 62 → 16 (подложка пиктограммы).
 * Доля у них разная — 0,27 / 0,31 / 0,26, — поэтому не вычисляем, а берём.
 */
const TILE_RADIUS: Record<number, number> = { 22: 6, 26: 8, 62: 16 }

const radiusOf = (size: number): number => TILE_RADIUS[size] ?? Math.round(size * 0.27)

/**
 * Цветная метка без содержимого: строка источника (22) и метка проекта (26).
 *
 * Иконки внутри у неё нет, и это не упущение — в макете там место под
 * фавикон источника и под цвет темы проекта. Иконка размером 9px, которая
 * получалась бы делением, читается как грязное пятно.
 */
export function Tile({
  size = 22,
  color = 'var(--line-soft)',
}: {
  size?: number
  color?: string
}) {
  return (
    <span
      className="icon-tile"
      style={{ width: size, height: size, borderRadius: radiusOf(size), background: color, flex: 'none' }}
    />
  )
}

/**
 * Плитка-подложка под пиктограмму — карточка задачи. В макете 62×62,
 * радиус 16, иконка внутри 24.
 */
export function IconTile({
  name,
  size = 62,
  background = 'var(--accent-soft)',
  color = 'var(--accent)',
}: {
  name: IconName
  size?: number
  background?: string
  color?: string
}) {
  return (
    <span
      className="icon-tile"
      style={{ width: size, height: size, borderRadius: radiusOf(size), background, color }}
    >
      <Icon name={name} size={Math.round(size * 0.39)} />
    </span>
  )
}
